export type BusinessDomain = 'LENDING' | 'CAPITALS';

export type NavGroup = 'Ops' | 'Collections' | 'Reports' | 'System' | 'Markets' | 'Tools';

export const BUSINESS_DOMAIN_STORAGE_KEY = 'whatzneu_business_domain';

export const BUSINESS_DOMAINS: { id: BusinessDomain; label: string; description: string }[] = [
  { id: 'LENDING', label: 'Lending', description: 'Loans, collections & finance ops' },
  { id: 'CAPITALS', label: 'Capitals', description: 'Control centre · stocks & capital ops' },
];

export const DOMAIN_DEFAULT_ROUTES: Record<BusinessDomain, string> = {
  LENDING: '/admin/dashboard',
  CAPITALS: '/admin/capitals/control-centre',
};

export function isCapitalsRoute(pathname: string): boolean {
  return pathname.startsWith('/admin/capitals')
    || pathname.startsWith('/admin/icici-breeze')
    || pathname.startsWith('/admin/stock-registry')
    || pathname.startsWith('/admin/networth');
}

export function domainForPath(pathname: string): BusinessDomain {
  return isCapitalsRoute(pathname) ? 'CAPITALS' : 'LENDING';
}

export interface AdminNavItem {
  id: string;
  label: string;
  iconName: string;
  color: string;
  subLabel: string;
  domains: BusinessDomain[];
  group?: NavGroup;
}

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { id: '/admin/dashboard', label: 'Dashboard', iconName: 'Analytics', color: '#0F766E', subLabel: 'Business Overview', domains: ['LENDING'], group: 'Ops' },
  { id: '/admin/customers', label: 'Customers', iconName: 'People', color: '#0F766E', subLabel: 'Manage Clients', domains: ['LENDING'], group: 'Ops' },
  { id: '/admin/payments', label: 'Payments', iconName: 'Payment', color: '#0F766E', subLabel: 'All Payments & Debits', domains: ['LENDING'], group: 'Ops' },
  { id: '/admin/leads', label: 'Leads', iconName: 'ContactMail', color: '#0F766E', subLabel: 'Loan Applications', domains: ['LENDING'], group: 'Ops' },
  { id: '/admin/products', label: 'Products', iconName: 'Inventory', color: '#0F766E', subLabel: 'Manage Products', domains: ['LENDING'], group: 'Ops' },

  { id: '/admin/blacklisted-customers', label: 'Blacklisted', iconName: 'Block', color: '#B91C1C', subLabel: 'Written-off / blocked borrowers', domains: ['LENDING'], group: 'Collections' },
  { id: '/admin/eligibility-check', label: 'Eligibility', iconName: 'FactCheck', color: '#0369A1', subLabel: 'Blocklist check by email / mobile', domains: ['LENDING'], group: 'Collections' },
  { id: '/admin/deposits-monitoring', label: 'Deposits', iconName: 'Savings', color: '#0F766E', subLabel: 'Report & audit trail', domains: ['LENDING'], group: 'Collections' },
  { id: '/admin/whatsapp-payment-reminders', label: 'WA Reminders', iconName: 'WhatsApp', color: '#15803D', subLabel: 'Approve & send payment reminders', domains: ['LENDING'], group: 'Collections' },
  { id: '/admin/interest-requests', label: 'Interest Requests', iconName: 'Favorite', color: '#D97706', subLabel: 'Customer Interests', domains: ['LENDING'], group: 'Collections' },

  { id: '/admin/monthly-payment-transactions', label: 'Monthly Txn Summary', iconName: 'CalendarMonth', color: '#475569', subLabel: 'Transactions by month (till today)', domains: ['LENDING'], group: 'Reports' },
  { id: '/admin/customer-lending-audit', label: 'Lending audit', iconName: 'Assessment', color: '#475569', subLabel: 'INTEREST vs short-term, expected interest', domains: ['LENDING'], group: 'Reports' },
  { id: '/admin/capital-efficiency', label: 'Capital efficiency', iconName: 'Speed', color: '#15803D', subLabel: 'CE% ranking & allocation suggestions', domains: ['LENDING'], group: 'Reports' },
  { id: '/admin/monthly-capital-efficiency', label: 'Monthly CE (MCE)', iconName: 'Timeline', color: '#0F766E', subLabel: 'Monthly snapshots, trends & flags', domains: ['LENDING'], group: 'Reports' },
  { id: '/admin/financial-summary', label: 'Financial Summary', iconName: 'AccountBalance', color: '#0F766E', subLabel: 'Annual Revenue & Balance Sheet', domains: ['LENDING'], group: 'Reports' },
  { id: '/admin/monthly-kpi-snapshots', label: 'Monthly KPIs', iconName: 'ShowChart', color: '#0369A1', subLabel: 'Month-end snapshots & trends', domains: ['LENDING'], group: 'Reports' },
  { id: '/admin/revenue-cashflow', label: 'Revenue by Customer', iconName: 'Receipt', color: '#D97706', subLabel: 'Revenue & Cashflow Report', domains: ['LENDING'], group: 'Reports' },
  { id: '/admin/interest-collected-month', label: 'Interest This Month', iconName: 'TrendingUp', color: '#0F766E', subLabel: 'Interest by Customer (till today)', domains: ['LENDING'], group: 'Reports' },
  { id: '/admin/oflync-cashflow', label: 'Oflync cashflow', iconName: 'CloudSync', color: '#0369A1', subLabel: 'Monthly Oflync connector log', domains: ['LENDING'], group: 'Reports' },

  { id: '/admin/capitals/control-centre', label: 'Control Centre', iconName: 'Hub', color: '#0F172A', subLabel: 'Capital ops oversight hub', domains: ['CAPITALS'], group: 'Markets' },
  { id: '/admin/capitals/stock-picks', label: 'Stock Picks', iconName: 'CandlestickChart', color: '#D97706', subLabel: 'Investment thesis & review', domains: ['CAPITALS'], group: 'Markets' },
  { id: '/admin/capitals/analytics', label: 'Margin & maturity', iconName: 'AccountBalanceWallet', color: '#0369A1', subLabel: 'Reporting · deployment · warnings', domains: ['CAPITALS'], group: 'Markets' },
  { id: '/admin/icici-breeze', label: 'ICICI Breeze', iconName: 'CurrencyExchange', color: '#0369A1', subLabel: 'Portfolio & collateral (NSE)', domains: ['CAPITALS'], group: 'Markets' },
  { id: '/admin/stock-registry', label: 'Stock Registry', iconName: 'ShowChart', color: '#0F766E', subLabel: 'NSE/BSE master & analysis', domains: ['CAPITALS'], group: 'Tools' },
  { id: '/admin/networth', label: 'Net worth', iconName: 'AccountBalanceWallet', color: '#475569', subLabel: 'Personal assets & snapshots', domains: ['LENDING', 'CAPITALS'], group: 'Tools' },

  { id: '/admin/settings', label: 'Settings', iconName: 'Settings', color: '#64748B', subLabel: 'Account Settings', domains: ['LENDING', 'CAPITALS'], group: 'System' },
];

export function navItemsForDomain(domain: BusinessDomain): AdminNavItem[] {
  return ADMIN_NAV_ITEMS.filter((item) => item.domains.includes(domain));
}

/** True when pathname matches a nav item allowed for the given domain (exact or nested). */
export function pathBelongsToDomain(pathname: string, domain: BusinessDomain): boolean {
  return navItemsForDomain(domain).some(
    (item) => pathname === item.id || pathname.startsWith(`${item.id}/`)
  );
}

export function groupedNavItems(domain: BusinessDomain): { group: NavGroup; items: AdminNavItem[] }[] {
  const items = navItemsForDomain(domain);
  const order: NavGroup[] =
    domain === 'LENDING'
      ? ['Ops', 'Collections', 'Reports', 'Tools', 'System']
      : ['Markets', 'Tools', 'System'];
  return order
    .map((group) => ({
      group,
      items: items.filter((i) => (i.group ?? 'Ops') === group),
    }))
    .filter((g) => g.items.length > 0);
}
